import { Lock } from "lucide-react";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { cn } from "lib/utils";
import React from "react";
import { useAssistantPermissionsStore } from "app/zustand/assistantPermissionsStore";

interface LockOverlayProps {
  isLock: boolean;
  // Only rendered by the "subscription" variant - omit it for "permission".
  featureName?: string;
  planName?: string;
  /**
   * "subscription" (default): icon + "Subscribe to Unlock" + description + Subscribe button.
   * Use when access requires upgrading a billing plan.
   * "permission": icon + description only, no title, no button.
   * Use when access is denied by role/permission (e.g. an assistant without
   * the required permission) rather than by a missing subscription.
   */
  variant?: "subscription" | "permission";
  className?: string;
  children: React.ReactNode;
}

/**
 * Wraps `children` and, when `isLock` is true, overlays a blur/lock UI on top
 * of them. Fills its parent (h-full w-full) so it takes over sizing duties
 * from whatever it wraps (e.g. a Card that used to rely on h-full to fill a
 * grid cell) — pass `className` to override.
 */
export function LockOverlay({
  isLock,
  featureName,
  planName = "Elite",
  variant = "permission",
  className,
  children,
}: LockOverlayProps) {
  const navigate = useNavigate();
  const isSubscriptionActive = useAssistantPermissionsStore((state) => state.isSubscriptionActive);

  return (
    <div className={cn("relative h-full w-full", className)}>
      {children}
      {isLock && (
        <div className="absolute inset-0 z-[1000] bg-black/50 flex items-center justify-center rounded-lg backdrop-blur-sm">
          <div className="text-center px-2">
            <Lock className="w-10 h-10 text-white mx-auto mb-2" />
            {variant === "subscription" ? (
              <>
                <p className="text-white font-medium">Subscribe to Unlock</p>
                <p className="text-white text-sm mt-1">
                  Please subscribe to <strong>{planName}</strong> plan to access the{" "}
                  <strong>{featureName}</strong>.
                </p>
                <Button
                  label="Subscribe"
                  size="small"
                  className="mt-2 p-1 px-2.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/profile/billing");
                  }}
                />
              </>
            ) : (
              <p className="text-white text-sm">
                {isSubscriptionActive
                  ? "Advisor has restricted your access to this section."
                  : "Your Assistant subscription is inactive. Subscribe to access this section."}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LockOverlay;
